/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { sort, alias } from '@ember/object/computed';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { getCurrentMilestone } from '@linc-technologies/linced-common/utils/progressions';
import { service } from '@ember/service';

export default class SubjectController extends Controller {
  Jurisdiction = Jurisdiction;

  @service
  session;

  @service
  dates;

  @alias('session.school')
  school;

  @alias('school.jurisdiction')
  jurisdiction;

  @computed('model.{group.name,subject.label}', 'session.school.name')
  get exportTitle() {
    return `${this.get('session.school.name')}--${
      this.model.group.name
    }--Progress Snapshots--${this.model.subject.label}`;
  }

  filterField = 'goalSubject.id';

  @computed('model.goalSubjects.[]')
  get subjects() {
    return this.model.goalSubjects
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @computed('model.levels.[]')
  get levels() {
    return this.model.levels
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @computed('levels.@each.id')
  get levelIds() {
    return this.levels.map((item) => item.id);
  }

  @alias('model.subject.milestones')
  milestones;

  @computed('model.learners.[]', 'milestones.[]')
  get displayMilestones() {
    let indices = [];
    this.model.learners.forEach((learner) => {
      let index = this.milestones.indexOf(
        getCurrentMilestone(
          this.milestones,
          learner,
          this.dates.rolloverMonthDate,
        ),
      );
      if (index >= 0) {
        // Don't push -1 if it wasn't found.
        indices.push(index);
      }
    });

    // Display a wide enough range to encompass all learners' current milestones,
    // potentially with some additional milestones before the lowest (for comparison).
    let min = Math.min(...indices);
    let max = Math.max(...indices);

    if (min > 0) {
      // Include a couple of previous milestones (for comparison) if available.
      min = Math.max(min - 2, 0);
    }

    // If we've still only got a few, pad up to 6 (if earlier milestones exist).
    let num = max - min + 1;
    if (num < 6 && min > 0) {
      min = Math.max(0, max - 5);
    }

    return this.milestones.slice(min, max + 1);
  }

  @computed('displayMilestones.[]')
  get displayMilestoneIds() {
    return this.displayMilestones.map((item) => item.id);
  }

  @alias('model.learners.length')
  numLearners;

  learnersSortOrder = Object.freeze(['firstName', 'lastName']);

  @sort('model.learners', 'learnersSortOrder')
  sortedLearners;

  @tracked
  detailData;

  @tracked
  detailDialogOpen;

  @tracked
  detailDirection;

  @tracked
  detailMilestone;

  @computed('sortedLearners', 'model.progressions.[]', 'displayMilestones.[]')
  get learners() {
    return this.sortedLearners.reduce((learners, learner) => {
      let progressions = this.model.progressions.filter(
        (item) => item.person_id === learner.get('id'),
      );
      let data = [];
      let previousLevelIndex = -1;
      this.displayMilestones.forEach((milestone) => {
        let level = null;
        let status = '';
        let levelIndex = -1;
        let missing = previousLevelIndex >= 0; // Only missing if we already have data for an earlier milestone.
        let record = progressions.find(
          (item) => item.milestone_id === milestone.id,
        );
        if (!isEmpty(record)) {
          level = record.get('level');
          missing = false;
          levelIndex = this.levelIds.indexOf(level.get('id'));

          if (previousLevelIndex >= 0 && levelIndex >= 0) {
            switch (true) {
              case levelIndex === previousLevelIndex:
                status = 'progress-none';
                break;
              case levelIndex < previousLevelIndex:
                status = 'progress-down';
                break;
              case levelIndex > previousLevelIndex:
                status = 'progress-up';
                break;
            }
          }

          previousLevelIndex = levelIndex;
        }

        data.push({
          milestoneId: milestone.id,
          person: learner,
          missing,
          level,
          status,
        });
      });

      learners.push({
        person: learner,
        data,
      });
      return learners;
    }, []);
  }

  @computed('model.progressions.[]', 'displayMilestones.[]', 'learners.[]')
  get summary() {
    let allData = [];
    this.learners.forEach((learner) => {
      // Push all actually-present data points (i.e. those with a Level) into the master list.
      allData.push(...learner.data.filter((item) => item.level));
    });
    return this.displayMilestones.reduce((milestones, milestone) => {
      let levels = [];
      let data = allData.filter((item) => item.milestoneId === milestone.id);
      this.levels.forEach((level) => {
        let levelData = data.filter((item) => item.level.id === level.id);
        levels.push({
          label: level.label,
          total: levelData.length,
          up: levelData.filter((item) => item.status === 'progress-up'),
          down: levelData.filter((item) => item.status === 'progress-down'),
          same: levelData.filter((record) => {
            // Include initial data points (no status, but has a level) with actual non-movers.
            return (
              record.status === 'progress-none' ||
              (record.status === '' && !isEmpty(record.level))
            );
          }),
        });
      });
      milestones.push({
        label: milestone.label,
        data: levels,
      });
      return milestones;
    }, []);
  }

  @computed('learners.[]')
  get exportData() {
    return this.learners.map((learner) => {
      let person = learner.person.getProperties([
        'id',
        'jurisdictionalId',
        'firstName',
        'lastName',
        'currentYearLevel',
        'gender',
        'displayEthnicity',
        'atsi',
      ]);
      let ethnicity;
      if (this.jurisdiction === Jurisdiction.AU_NSW) {
        ethnicity = person.atsi;
      } else {
        ethnicity = person.displayEthnicity;
      }
      let row = [
        person.jurisdictionalId,
        person.firstName,
        person.lastName,
        person.currentYearLevel,
        person.gender,
        ethnicity,
      ];

      learner.data.forEach((data) => {
        if (isEmpty(data.level)) {
          row.push('');
        } else {
          row.push(data.level.get('label'));
        }
      });

      return row;
    });
  }

  @action
  getPercentageOfLearners(num) {
    let result = 0;
    if (num <= 0 || this.numLearners <= 0) {
      return result;
    }

    result = (num / this.numLearners) * 100;
    return result.toFixed(2);
  }

  @action
  openDetailDialog(data, direction, milestone) {
    this.detailData = data;
    this.detailDirection = direction;
    this.detailMilestone = milestone;
    this.detailDialogOpen = true;
  }
}
